/**
 * chartLiveData — re-resolve chart series values against LIVE sheet data.
 *
 * Parsed charts carry the file's CACHED series values plus (when present)
 * the source-range formula, e.g. "Sheet1!$B$2:$B$13".  After a reader edits
 * cells, the cache is stale — this module recomputes each series' data from
 * the current sheet model so graphs reflect the edits (QA #4).
 *
 * Anything that can't be resolved (multi-area refs, missing sheet, no ref)
 * keeps its cached values — rendering never breaks on exotic charts.
 */

function colLabelToIndex(label) {
  let n = 0;
  for (let i = 0; i < label.length; i += 1) {
    n = n * 26 + (label.toUpperCase().charCodeAt(i) - 64);
  }
  return n - 1;
}

// "$B$2" / "B2" -> { row, col } (0-based) or null.
function parseCellRef(ref) {
  const m = /^\$?([A-Za-z]+)\$?(\d+)$/.exec(String(ref).trim());
  if (!m) return null;
  return { row: parseInt(m[2], 10) - 1, col: colLabelToIndex(m[1]) };
}

/**
 * "Sheet1!$B$2:$B$13" or "'My Sheet'!$B$2:$B$13" ->
 * { sheet, r1, c1, r2, c2 } or null (multi-area refs are rejected).
 */
export function parseSheetRange(ref) {
  if (!ref || String(ref).indexOf(",") !== -1) return null;
  const m = /^(?:'((?:[^']|'')+)'|([^'!]+))!(\$?[A-Za-z]+\$?\d+)(?::(\$?[A-Za-z]+\$?\d+))?$/.exec(String(ref).trim());
  if (!m) return null;
  const sheet = m[1] ? m[1].replace(/''/g, "'") : m[2];
  const a = parseCellRef(m[3]);
  const b = m[4] ? parseCellRef(m[4]) : a;
  if (!a || !b) return null;
  return {
    sheet,
    r1: Math.min(a.row, b.row), r2: Math.max(a.row, b.row),
    c1: Math.min(a.col, b.col), c2: Math.max(a.col, b.col),
  };
}

// Walk the range row-major -> [{ cur, changed }] where `changed` says the
// cell differs from the workbook as LOADED (model.baseRows). Only changed
// cells override the chart's cached points, so an unedited chart renders
// exactly as Excel saved it (number formats, dates, #N/A gaps, …) and an
// edited cell shows its new value immediately.
function readRange(model, rng) {
  const rows = model.rows || [];
  const base = model.baseRows;
  const out = [];
  for (let r = rng.r1; r <= rng.r2; r += 1) {
    for (let c = rng.c1; c <= rng.c2; c += 1) {
      const v = rows[r] ? rows[r][c] : null;
      const b = base ? (base[r] ? base[r][c] : null) : v;
      const cur = v == null ? "" : v;
      const orig = b == null ? "" : b;
      out.push({ cur, changed: !base || String(cur) !== String(orig) });
    }
  }
  return out;
}

const toNumber = (v) => {
  const num = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(num) ? num : 0; // blank/text -> 0, like the numCache reader
};

const sameArray = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Return the charts array with every resolvable series' data (and the
 * category labels) updated from the live values in `sheetDataByName`.
 * Charts/series without a usable ref are passed through unchanged (same
 * object identity when nothing changed, so memoized charts don't redraw).
 */
export function withLiveChartData(charts, sheetDataByName) {
  if (!Array.isArray(charts) || charts.length === 0 || !sheetDataByName) return charts;
  return charts.map((chart) => {
    if (!chart || chart.isSparkline || !Array.isArray(chart.series) || chart.series.length === 0) return chart;
    let changed = false;
    let series = chart.series.map((s) => {
      if (!s || !s.valuesRef) return s;
      const rng = parseSheetRange(s.valuesRef);
      if (!rng) return s;
      const model = sheetDataByName[rng.sheet];
      if (!model || !Array.isArray(model.rows)) return s;
      const cells = readRange(model, rng);
      if (cells.length === 0) return s;
      const cached = Array.isArray(s.data) ? s.data : [];
      const live = cells.map((cell, i) =>
        (cell.changed || i >= cached.length ? toNumber(cell.cur) : cached[i]));
      if (sameArray(live, cached)) return s;
      changed = true;
      return { ...s, data: live };
    });

    // A series written without a value cache whose range could not be read
    // has nothing to draw — drop it rather than feed renderers an empty array.
    const nonEmpty = series.filter((s) => !s || !Array.isArray(s.data) || s.data.length > 0);
    if (nonEmpty.length !== series.length) { series = nonEmpty; changed = true; }

    // Live category labels (single-level categories only).
    let categories = chart.categories;
    const catRefSeries = series.find((s) => s && s.categoriesRef);
    if (catRefSeries && !chart.catLevels) {
      const rng = parseSheetRange(catRefSeries.categoriesRef);
      const model = rng ? sheetDataByName[rng.sheet] : null;
      if (model && Array.isArray(model.rows)) {
        const cells = readRange(model, rng);
        const cached = Array.isArray(chart.categories) ? chart.categories : [];
        const labels = cells.map((cell, i) =>
          (cell.changed || i >= cached.length ? String(cell.cur) : cached[i]));
        if (labels.length > 0 && !sameArray(labels, cached)) {
          categories = labels;
          changed = true;
        }
      }
    }

    if (!changed) return chart;
    return { ...chart, series, categories, placeholder: series.length === 0 ? true : chart.placeholder };
  });
}

export default withLiveChartData;
